import React, { useEffect, useState } from "react";
import ReviewCard from "./ReviewCard";

interface Review {
    id: number;
    customer_name: string;
    customer_image?: string | null;
    rating: number;
    review_text: string;
    product_name?: string | null;
    is_verified: boolean;
    created_at: string;
}

// const API_URL = "http://localhost:8483";
const API_URL = "https://www.ayushvatika.com";


const Reviews: React.FC = () => {

    const [reviews, setReviews] = useState<Review[]>([]);
    const [loading, setLoading] = useState(true);


    useEffect(() => {

        const fetchReviews = async () => {

            try {

                const response = await fetch(
                    `${API_URL}/api/reviews`
                );

                const data = await response.json();

                if (data.success) {
                    setReviews(data.reviews);
                }

            } catch (error) {

                console.error(
                    "Failed to load reviews:",
                    error
                );

            } finally {

                setLoading(false);

            }
        };


        fetchReviews();

    }, []);


    const averageRating =
        reviews.length > 0
            ? (
                reviews.reduce(
                    (sum, review) => sum + review.rating,
                    0
                ) / reviews.length
            ).toFixed(1)
            : "0.0";


    return (

        <main className="bg-white min-h-screen">


            {/* =====================================
                HERO
            ===================================== */}

            <section className="
                bg-gradient-to-b
                from-green-50
                via-green-50/40
                to-white
                px-4
                pt-12
                pb-10
            ">

                <div className="
                    max-w-6xl
                    mx-auto
                    text-center
                ">

                    <p className="
                        text-green-700
                        font-semibold
                        uppercase
                        tracking-[0.18em]
                        text-xs
                    ">
                        Customer Experiences
                    </p>


                    <h1 className="
                        text-3xl
                        md:text-4xl
                        lg:text-5xl
                        font-bold
                        text-gray-900
                        mt-2
                    ">
                        What Our Customers Say
                    </h1>


                    <p className="
                        text-gray-600
                        max-w-xl
                        mx-auto
                        mt-3
                        text-sm
                        md:text-base
                        leading-6
                    ">
                        Discover genuine experiences shared by
                        customers who have chosen Ayushvatika.
                    </p>


                    {/* Rating Summary */}

                    {!loading && reviews.length > 0 && (

                        <div className="
                            inline-flex
                            items-center
                            gap-5
                            mt-6
                            bg-white
                            border border-gray-200
                            rounded-2xl
                            px-5
                            py-3.5
                            shadow-sm
                        ">

                            {/* Average */}

                            <div className="text-center">

                                <div className="
                                    text-2xl
                                    font-bold
                                    text-gray-900
                                    leading-none
                                ">
                                    {averageRating}
                                </div>

                                <div className="
                                    flex
                                    justify-center
                                    mt-1
                                    gap-0.5
                                ">

                                    {[1, 2, 3, 4, 5].map(star => (

                                        <span
                                            key={star}
                                            className="
                                                text-[#fbbc04]
                                                text-sm
                                            "
                                        >
                                            ★
                                        </span>

                                    ))}

                                </div>

                            </div>


                            {/* Divider */}

                            <div className="
                                h-9
                                w-px
                                bg-gray-200
                            " />


                            {/* Count */}

                            <div className="text-left">

                                <p className="
                                    font-semibold
                                    text-gray-900
                                    text-sm
                                ">
                                    Customer Reviews
                                </p>

                                <p className="
                                    text-xs
                                    text-gray-500
                                    mt-0.5
                                ">
                                    {reviews.length} published
                                    {reviews.length === 1
                                        ? " review"
                                        : " reviews"}
                                </p>

                            </div>

                        </div>

                    )}

                </div>

            </section>



            {/* =====================================
                REVIEWS
            ===================================== */}

            <section className="
                max-w-7xl
                mx-auto
                px-4
                sm:px-6
                lg:px-8
                pt-8
                pb-14
            ">

                {loading ? (

                    <div className="
                        grid
                        grid-cols-1
                        md:grid-cols-2
                        lg:grid-cols-3
                        gap-5
                    ">

                        {[1, 2, 3].map(item => (

                            <div
                                key={item}
                                className="
                                    h-56
                                    bg-gray-100
                                    rounded-2xl
                                    animate-pulse
                                "
                            />

                        ))}

                    </div>

                ) : reviews.length === 0 ? (

                    <div className="
                        flex
                        flex-col
                        items-center
                        justify-center
                        py-16
                        text-center
                    ">

                        <div className="
                            w-14
                            h-14
                            rounded-full
                            bg-green-50
                            flex
                            items-center
                            justify-center
                            text-2xl
                            mb-3
                        ">
                            ⭐
                        </div>

                        <h2 className="
                            text-xl
                            font-semibold
                            text-gray-900
                        ">
                            No reviews yet
                        </h2>

                        <p className="
                            text-sm
                            text-gray-500
                            mt-1
                        ">
                            Customer reviews will appear here.
                        </p>

                    </div>

                ) : (

                    <div className="
                        grid
                        grid-cols-1
                        md:grid-cols-2
                        lg:grid-cols-3
                        gap-5
                        items-stretch
                    ">

                        {reviews.map(review => (

                            <ReviewCard
                                key={review.id}
                                review={review}
                            />

                        ))}

                    </div>

                )}

            </section>

        </main>

    );
};

export default Reviews;